import { createFileRoute } from "@tanstack/react-router";
import { useServerFn } from "@tanstack/react-start";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useState } from "react";
import { toast } from "sonner";
import {
  adminExportPaymentEventsCsv,
  adminListPaymentEvents,
  adminListReceiptJobs,
  adminProcessReceiptQueue,
  adminReconcilePayfast,
  adminResendPayfastReceipt,
  adminTestLeadMagnetFlow,
} from "@/lib/api/payments-admin.functions";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Link } from "@tanstack/react-router";
import { Download, FileDown, RefreshCw, Send, Mail, Bug, CheckCircle2, XCircle, ShieldCheck } from "lucide-react";

export const Route = createFileRoute("/_authenticated/portal/admin/payment-events")(
  {
    component: PaymentEventsAdmin,
  },
);

function StatusPill({ ok, error }: { ok: boolean; error?: string | null }) {
  return ok ? (
    <span className="inline-flex items-center gap-1 rounded-full bg-emerald-100 px-2 py-0.5 text-[11px] font-semibold text-emerald-800">
      <CheckCircle2 className="h-3 w-3" /> processed
    </span>
  ) : (
    <span
      className="inline-flex items-center gap-1 rounded-full bg-rose-100 px-2 py-0.5 text-[11px] font-semibold text-rose-800"
      title={error ?? ""}
    >
      <XCircle className="h-3 w-3" /> failed
    </span>
  );
}

function PaymentEventsAdmin() {
  const list = useServerFn(adminListPaymentEvents);
  const listJobs = useServerFn(adminListReceiptJobs);
  const drain = useServerFn(adminProcessReceiptQueue);
  const resend = useServerFn(adminResendPayfastReceipt);
  const testLead = useServerFn(adminTestLeadMagnetFlow);
  const exportCsv = useServerFn(adminExportPaymentEventsCsv);
  const reconcile = useServerFn(adminReconcilePayfast);
  const qc = useQueryClient();

  const [filters, setFilters] = useState({
    reference: "",
    eventType: "",
    processed: "all" as "all" | "yes" | "no",
    from: "",
    to: "",
    page: 1,
    pageSize: 25,
  });

  const { data, isLoading } = useQuery({
    queryKey: ["payment-events", filters],
    queryFn: () =>
      list({
        data: {
          reference: filters.reference || undefined,
          eventType: filters.eventType || undefined,
          processed: filters.processed,
          from: filters.from ? new Date(filters.from).toISOString() : undefined,
          to: filters.to ? new Date(filters.to).toISOString() : undefined,
          page: filters.page,
          pageSize: filters.pageSize,
        },
      }),
  });

  const { data: jobs } = useQuery({
    queryKey: ["receipt-jobs"],
    queryFn: () => listJobs(),
  });

  const drainMut = useMutation({
    mutationFn: () => drain(),
    onSuccess: (r) => {
      toast.success(`Queue drained: ${r.sent} sent, ${r.retried} retry, ${r.failed} failed`);
      qc.invalidateQueries({ queryKey: ["receipt-jobs"] });
      qc.invalidateQueries({ queryKey: ["payment-events"] });
    },
    onError: (e: any) => toast.error(e?.message ?? "Failed"),
  });

  const resendMut = useMutation({
    mutationFn: (ref: string) => resend({ data: { reference: ref } }),
    onSuccess: (r) => {
      if (r.ok) toast.success(`Receipt re-sent to ${r.email}`);
      else toast.warning(`Queued for retry — ${r.reason}`);
      qc.invalidateQueries({ queryKey: ["receipt-jobs"] });
    },
    onError: (e: any) => toast.error(e?.message ?? "Resend failed"),
  });

  const [resendRef, setResendRef] = useState("");
  const [testEmail, setTestEmail] = useState("");
  const [testReport, setTestReport] = useState<any>(null);
  const testMut = useMutation({
    mutationFn: (vars: { email: string; dryRun: boolean }) =>
      testLead({ data: vars }),
    onSuccess: (r) => {
      setTestReport(r);
      if (r.ok) toast.success("Lead-magnet flow check passed");
      else toast.warning("Lead-magnet flow has issues — see report");
    },
    onError: (e: any) => toast.error(e?.message ?? "Test failed"),
  });

  const exportMut = useMutation({
    mutationFn: () =>
      exportCsv({
        data: {
          reference: filters.reference || undefined,
          eventType: filters.eventType || undefined,
          processed: filters.processed,
          from: filters.from ? new Date(filters.from).toISOString() : undefined,
          to: filters.to ? new Date(filters.to).toISOString() : undefined,
        },
      }),
    onSuccess: (r) => {
      const blob = new Blob([r.csv], { type: "text/csv;charset=utf-8" });
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `payment-events-${new Date().toISOString().slice(0, 10)}.csv`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
      toast.success(`Exported ${r.count} rows`);
    },
    onError: (e: any) => toast.error(e?.message ?? "Export failed"),
  });

  const reconcileMut = useMutation({
    mutationFn: () => reconcile(),
    onSuccess: (r) => {
      toast.success(
        `Reconciled · ${r.complete_unpaid.reconciled} fixed, ${r.paid_no_receipt.enqueued} queued, ${r.queue.sent} sent`,
      );
      qc.invalidateQueries({ queryKey: ["payment-events"] });
      qc.invalidateQueries({ queryKey: ["receipt-jobs"] });
    },
    onError: (e: any) => toast.error(e?.message ?? "Reconcile failed"),
  });

  const rows = data?.rows ?? [];
  const total = data?.total ?? 0;
  const totalPages = Math.max(1, Math.ceil(total / filters.pageSize));

  return (
    <div className="space-y-6">
      <header>
        <h1 className="font-display text-2xl text-navy">Payment events & receipts</h1>
        <p className="text-sm text-muted-foreground">
          Audit every PayFast ITN, replay receipts, drain the retry queue, and
          smoke-test the lead-magnet flow.
        </p>
      </header>

      {/* Quick admin actions */}
      <section className="grid gap-4 md:grid-cols-3">
        <div className="rounded-xl border border-border bg-card p-4">
          <h2 className="flex items-center gap-2 font-semibold text-navy">
            <Send className="h-4 w-4" /> Re-send a receipt
          </h2>
          <p className="mt-1 text-xs text-muted-foreground">
            Customer says they didn't get the PayFast receipt? Enter the
            reference (NDL-CR-… or contract ref) and resend.
          </p>
          <div className="mt-2 flex gap-2">
            <Input
              value={resendRef}
              onChange={(e) => setResendRef(e.target.value)}
              placeholder="NDL-CR-…"
              className="font-mono text-xs"
            />
            <Button
              size="sm"
              disabled={!resendRef || resendMut.isPending}
              onClick={() => resendMut.mutate(resendRef.trim())}
            >
              {resendMut.isPending ? "…" : "Resend"}
            </Button>
          </div>
        </div>

        <div className="rounded-xl border border-border bg-card p-4">
          <h2 className="flex items-center gap-2 font-semibold text-navy">
            <RefreshCw className="h-4 w-4" /> Receipt retry queue
          </h2>
          <p className="mt-1 text-xs text-muted-foreground">
            {jobs?.rows.filter((j: any) => j.status === "pending").length ?? 0}{" "}
            pending · runs automatically every 5 min.
          </p>
          <Button
            size="sm"
            variant="outline"
            className="mt-2"
            onClick={() => drainMut.mutate()}
            disabled={drainMut.isPending}
          >
            {drainMut.isPending ? "Draining…" : "Drain now"}
          </Button>
        </div>

        <div className="rounded-xl border border-border bg-card p-4">
          <h2 className="flex items-center gap-2 font-semibold text-navy">
            <Bug className="h-4 w-4" /> Lead-magnet smoke test
          </h2>
          <p className="mt-1 text-xs text-muted-foreground">
            Verifies the CCMA PDF link, unsubscribe page + API, and (optionally) sends a real email.
          </p>
          <div className="mt-2 space-y-2">
            <Input
              type="email"
              value={testEmail}
              onChange={(e) => setTestEmail(e.target.value)}
              placeholder="you@example.com"
            />
            <div className="flex gap-2">
              <Button
                size="sm"
                variant="outline"
                disabled={!testEmail || testMut.isPending}
                onClick={() => testMut.mutate({ email: testEmail, dryRun: true })}
              >
                Dry-run
              </Button>
              <Button
                size="sm"
                disabled={!testEmail || testMut.isPending}
                onClick={() => testMut.mutate({ email: testEmail, dryRun: false })}
              >
                <Mail className="mr-1 h-3.5 w-3.5" /> Send email
              </Button>
            </div>
          </div>
        </div>
      </section>

      {testReport && (
        <pre className="overflow-auto rounded-lg border border-border bg-muted/40 p-3 text-[11px] leading-snug">
          {JSON.stringify(testReport, null, 2)}
        </pre>
      )}

      {/* Filters */}
      <section className="rounded-xl border border-border bg-card p-4">
        <div className="mb-3 flex flex-wrap items-center justify-between gap-2">
          <h2 className="font-semibold text-navy">All payment events</h2>
          <div className="flex flex-wrap gap-2">
            <Link
              to="/portal/admin/receipts-queue"
              className="inline-flex items-center gap-1 rounded-md border border-border bg-background px-3 py-1.5 text-xs text-navy hover:bg-muted"
            >
              <RefreshCw className="h-3.5 w-3.5" /> Open retry queue
            </Link>
            <Button
              size="sm"
              variant="secondary"
              onClick={() => reconcileMut.mutate()}
              disabled={reconcileMut.isPending}
            >
              <ShieldCheck className="mr-1 h-3.5 w-3.5" />
              {reconcileMut.isPending ? "Reconciling…" : "Reconcile PayFast"}
            </Button>
            <Button
              size="sm"
              onClick={() => exportMut.mutate()}
              disabled={exportMut.isPending}
            >
              <Download className="mr-1 h-3.5 w-3.5" />
              {exportMut.isPending ? "Building CSV…" : "Export CSV"}
            </Button>
          </div>
        </div>
        <div className="grid gap-3 md:grid-cols-5">
          <div>
            <Label className="text-xs">Reference contains</Label>
            <Input
              value={filters.reference}
              onChange={(e) => setFilters({ ...filters, reference: e.target.value, page: 1 })}
            />
          </div>
          <div>
            <Label className="text-xs">Event type</Label>
            <Input
              value={filters.eventType}
              onChange={(e) => setFilters({ ...filters, eventType: e.target.value, page: 1 })}
              placeholder="COMPLETE, signature_invalid, …"
            />
          </div>
          <div>
            <Label className="text-xs">Processed</Label>
            <Select
              value={filters.processed}
              onValueChange={(v) =>
                setFilters({ ...filters, processed: v as any, page: 1 })
              }
            >
              <SelectTrigger><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All</SelectItem>
                <SelectItem value="yes">Processed only</SelectItem>
                <SelectItem value="no">Failures only</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <div>
            <Label className="text-xs">From</Label>
            <Input
              type="date"
              value={filters.from}
              onChange={(e) => setFilters({ ...filters, from: e.target.value, page: 1 })}
            />
          </div>
          <div>
            <Label className="text-xs">To</Label>
            <Input
              type="date"
              value={filters.to}
              onChange={(e) => setFilters({ ...filters, to: e.target.value, page: 1 })}
            />
          </div>
        </div>
      </section>

      {/* Events table */}
      <section className="overflow-hidden rounded-xl border border-border bg-card">
        {isLoading ? (
          <p className="p-4 text-sm text-muted-foreground">Loading…</p>
        ) : rows.length === 0 ? (
          <p className="p-4 text-sm text-muted-foreground">No events match.</p>
        ) : (
          <table className="w-full text-xs">
            <thead className="bg-muted text-left uppercase tracking-wide text-muted-foreground">
              <tr>
                <th className="px-3 py-2">When</th>
                <th className="px-3 py-2">Provider · Event</th>
                <th className="px-3 py-2">Reference</th>
                <th className="px-3 py-2">Status</th>
                <th className="px-3 py-2">Failure reason</th>
                <th className="px-3 py-2 text-right">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-border">
              {rows.map((r: any) => (
                <tr key={r.id} className="align-top">
                  <td className="px-3 py-2 whitespace-nowrap">{new Date(r.created_at).toLocaleString("en-ZA")}</td>
                  <td className="px-3 py-2">
                    <p className="font-semibold">{r.provider}</p>
                    <p className="text-muted-foreground">{r.event_type}</p>
                  </td>
                  <td className="px-3 py-2 font-mono">{r.reference ?? "—"}</td>
                  <td className="px-3 py-2"><StatusPill ok={r.processed} error={r.error_message} /></td>
                  <td className="px-3 py-2 max-w-[280px] text-rose-700">{r.error_message ?? ""}</td>
                  <td className="px-3 py-2 text-right whitespace-nowrap">
                    {r.reference && (
                      <>
                        <a
                          href={`/payment-status/${encodeURIComponent(r.reference)}`}
                          target="_blank" rel="noopener"
                          className="mr-2 inline-flex items-center gap-1 text-navy hover:underline"
                          title="Open order to download receipt"
                        >
                          <FileDown className="h-3 w-3" /> Receipt
                        </a>

                        <button
                          onClick={() => resendMut.mutate(r.reference)}
                          className="inline-flex items-center gap-1 text-navy hover:underline"
                        >
                          <Send className="h-3 w-3" /> Resend
                        </button>
                      </>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
        <div className="flex items-center justify-between border-t border-border px-3 py-2 text-xs">
          <span>
            {total} total · page {filters.page}/{totalPages}
          </span>
          <div className="flex items-center gap-2">
            <Select
              value={String(filters.pageSize)}
              onValueChange={(v) => setFilters({ ...filters, pageSize: Number(v), page: 1 })}
            >
              <SelectTrigger className="h-7 w-[80px]"><SelectValue /></SelectTrigger>
              <SelectContent>
                {[25, 50, 100].map((n) => (
                  <SelectItem key={n} value={String(n)}>{n}/page</SelectItem>
                ))}
              </SelectContent>
            </Select>
            <Button size="sm" variant="outline" disabled={filters.page <= 1} onClick={() => setFilters({ ...filters, page: filters.page - 1 })}>Prev</Button>
            <Button size="sm" variant="outline" disabled={filters.page >= totalPages} onClick={() => setFilters({ ...filters, page: filters.page + 1 })}>Next</Button>
          </div>
        </div>
      </section>

      {/* Retry queue */}
      <section>
        <h2 className="mb-2 font-semibold text-navy">Receipt retry queue</h2>
        {!jobs?.rows.length ? (
          <p className="text-sm text-muted-foreground">Queue is empty.</p>
        ) : (
          <div className="overflow-hidden rounded-xl border border-border bg-card">
            <table className="w-full text-xs">
              <thead className="bg-muted text-left uppercase tracking-wide text-muted-foreground">
                <tr>
                  <th className="px-3 py-2">Created</th>
                  <th className="px-3 py-2">Reference</th>
                  <th className="px-3 py-2">Email</th>
                  <th className="px-3 py-2">Status</th>
                  <th className="px-3 py-2">Attempts</th>
                  <th className="px-3 py-2">Next attempt</th>
                  <th className="px-3 py-2">Last error</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-border">
                {jobs.rows.map((j: any) => (
                  <tr key={j.id}>
                    <td className="px-3 py-2 whitespace-nowrap">{new Date(j.created_at).toLocaleString("en-ZA")}</td>
                    <td className="px-3 py-2 font-mono">{j.reference}</td>
                    <td className="px-3 py-2">{j.buyer_email}</td>
                    <td className="px-3 py-2">{j.status}</td>
                    <td className="px-3 py-2">{j.attempts}/{j.max_attempts}</td>
                    <td className="px-3 py-2 whitespace-nowrap">{j.next_attempt_at ? new Date(j.next_attempt_at).toLocaleString("en-ZA") : "—"}</td>
                    <td className="px-3 py-2 text-rose-700">{j.last_error ?? ""}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </section>
    </div>
  );
}
